import React, { useMemo } from 'react';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  DigitalProfile,
  FraudReport,
  FraudSignal,
  PositiveSignal,
} from '../types';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  BigNumbers,
  Body,
  Label,
  SecondaryBody,
  SectionTitle,
  TertiaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import CheckIcon from '@grnhse/seedling/lib/birch/icons/check';
import ShieldExclamationIcon from '@grnhse/seedling/lib/birch/icons/shield_exclamation';
import WarningIcon from '@grnhse/seedling/lib/birch/icons/warning';
import FraudStatusBanner from './fraud_status_banner';
import { useFraudReportStatus } from 'fraud/hooks/useFraudReportStatus';

const t = withLocalePrefix('fraud.full_fraud_report');

const SignalListItem = ({
  description,
  explanation,
  icon,
}: {
  description: string;
  explanation?: string;
  icon: React.ReactNode;
}) => {
  return (
    <Grid data-provides="full-report-signal-list-item">
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={0}
        sx={{ paddingX: 2, paddingY: 1 }}
      >
        <Grid flex={1} sx={{ marginRight: 4.5 }}>
          <SecondaryBody medium>{description}</SecondaryBody>
          {explanation && <SecondaryBody metadata>{explanation}</SecondaryBody>}
        </Grid>
        {icon}
      </Grid>
      <Divider />
    </Grid>
  );
};

const PositiveSignals = ({ signals }: { signals: PositiveSignal[] }) => {
  return (
    <Stack spacing={3} data-provides="full-report-positive-signals">
      <Stack spacing={1}>
        <SectionTitle>{t('positive_signals.title')}</SectionTitle>
        <Body>{t('positive_signals.description')}</Body>
      </Stack>

      {signals.length === 0 && (
        <SignalCountGrid
          container
          direction="column"
          spacing={0.5}
          sx={{ paddingX: 1.5, paddingY: 0.75, margin: 0 }}
          data-provides="full-report-no-positive-signals"
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ margin: 0 }}
          >
            <CheckIcon
              color={FullPalette.EVERGREEN_700}
              size="md"
              data-provides="positive-signal-check-icon"
            />
            <BigNumbers>{0}</BigNumbers>
          </Grid>
          <Grid>
            <SecondaryBody>{t('positive_signals.title')}</SecondaryBody>
          </Grid>
        </SignalCountGrid>
      )}

      {signals.length > 0 && (
        <Stack spacing={0} data-provides="full-report-positive-signals-list">
          {signals.map((signal, index) => (
            <SignalListItem
              key={index}
              description={signal.description}
              explanation={signal.explanation}
              icon={
                <CheckIcon
                  color={FullPalette.GREEN_700}
                  size="sm"
                  data-provides="positive-signal-check-icon"
                />
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const FraudSignalSeverityGroup = ({
  type,
  count,
}: {
  type: 'strong' | 'weak';
  count: number;
}) => {
  const title =
    type === 'strong'
      ? t('fraud_signals.strong_signals')
      : t('fraud_signals.weak_signals');

  const icon =
    type === 'strong' ? (
      <ShieldExclamationIcon
        color={count > 0 ? FullPalette.POPPY_600 : FullPalette.EVERGREEN_700}
        size="md"
        data-provides="strong-signal-icon"
      />
    ) : (
      <WarningIcon
        color={count > 0 ? FullPalette.YELLOW_800 : FullPalette.EVERGREEN_700}
        size="md"
        data-provides="weak-signal-icon"
      />
    );

  return (
    <Grid flex={1} data-provides={`full-report-fraud-${type}-signals`}>
      <SignalCountGrid
        container
        direction="column"
        spacing={0.5}
        sx={{ paddingX: 1.5, paddingY: 0.75, margin: 0 }}
      >
        <Grid>
          <Label>{title}</Label>
        </Grid>
        <Grid container alignItems="center" spacing={0.75} sx={{ margin: 0 }}>
          <BigNumbers style={{ marginRight: Tokens.spacing[1] }}>
            {count}
          </BigNumbers>
          {icon}
        </Grid>
      </SignalCountGrid>
    </Grid>
  );
};

const FraudSignalCategoryGroup = ({
  title,
  signals,
}: {
  title: string;
  signals: FraudSignal[];
}) => {
  if (signals.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.5}>
      <Body medium>{title}</Body>
      <Stack
        spacing={0}
        data-provides={`full-report-fraud-signals-${signals[0].category}-list`}
      >
        {signals.map((signal, index) => (
          <SignalListItem
            key={index}
            description={signal.description}
            explanation={signal.explanation}
            icon={
              signal.severity === 'weak' ? (
                <WarningIcon
                  color={FullPalette.YELLOW_800}
                  size="sm"
                  data-provides="weak-signal-icon"
                />
              ) : (
                <ShieldExclamationIcon
                  color={FullPalette.POPPY_600}
                  size="sm"
                  data-provides="strong-signal-icon"
                />
              )
            }
          />
        ))}
      </Stack>
    </Stack>
  );
};

const FraudSignals = ({ signals }: { signals: FraudSignal[] }) => {
  const groupedSignals = useMemo(() => {
    const severityGroups = {
      strong: [] as FraudSignal[],
      weak: [] as FraudSignal[],
    };

    const categoryGroups = {
      location: [] as FraudSignal[],
      ip_address: [] as FraudSignal[],
      email: [] as FraudSignal[],
      phone: [] as FraudSignal[],
    };

    signals.forEach((signal) => {
      if (signal.severity) {
        severityGroups[signal.severity].push(signal);
      }
      categoryGroups[signal.category].push(signal);
    });

    return { severityGroups, categoryGroups };
  }, [signals]);

  const {
    severityGroups: { strong: strongSignals, weak: weakSignals },
    categoryGroups: {
      location: locationSignals,
      ip_address: ipAddressSignals,
      email: emailSignals,
      phone: phoneSignals,
    },
  } = groupedSignals;

  return (
    <Stack spacing={3} data-provides="full-report-fraud-signals">
      <Stack spacing={1}>
        <SectionTitle>{t('fraud_signals.title')}</SectionTitle>
        <Body>{t('fraud_signals.description')}</Body>
      </Stack>

      <Grid container spacing={0.5} justifyContent="space-between">
        <FraudSignalSeverityGroup type="strong" count={strongSignals.length} />
        <FraudSignalSeverityGroup type="weak" count={weakSignals.length} />
      </Grid>

      <FraudSignalCategoryGroup
        title={t('fraud_signals.location')}
        signals={locationSignals}
      />
      <FraudSignalCategoryGroup
        title={t('fraud_signals.ip_address')}
        signals={ipAddressSignals}
      />
      <FraudSignalCategoryGroup
        title={t('fraud_signals.email')}
        signals={emailSignals}
      />
      <FraudSignalCategoryGroup
        title={t('fraud_signals.phone')}
        signals={phoneSignals}
      />
    </Stack>
  );
};

const DigitalProfileItem = ({
  label,
  value,
  isPii = false,
}: {
  label: string;
  value: string | null;
  isPii?: boolean;
}) => {
  return (
    <Grid container direction="column" spacing={0.5} sx={{ margin: 0 }}>
      <Label>{label}</Label>
      <Body className={isPii ? 'pii-blur' : undefined}>{value || '--'}</Body>
    </Grid>
  );
};

const DigitalProfile = ({ profile }: { profile: DigitalProfile | null }) => {
  if (!profile) {
    return null;
  }

  return (
    <Stack spacing={2} data-provides="full-report-digital-profile">
      <Stack spacing={1}>
        <SectionTitle>{t('digital_profile.title')}</SectionTitle>
        <Body>{t('digital_profile.description')}</Body>
      </Stack>
      <DigitalProfileItem
        label={t('digital_profile.name')}
        value={profile.name}
        isPii={true}
      />
      <DigitalProfileItem
        label={t('digital_profile.email')}
        value={profile.email}
        isPii={true}
      />
      <DigitalProfileItem
        label={t('digital_profile.phone')}
        value={profile.phone}
        isPii={true}
      />
      <DigitalProfileItem
        label={t('digital_profile.phone_location')}
        value={profile.phone_location}
      />
      <DigitalProfileItem
        label={t('digital_profile.phone_carrier')}
        value={profile.phone_carrier}
      />
      <DigitalProfileItem
        label={t('digital_profile.ip_address')}
        value={profile.ip_address}
        isPii={true}
      />
      <DigitalProfileItem
        label={t('digital_profile.ip_connection_type')}
        value={profile.ip_connection_type}
      />
      <DigitalProfileItem
        label={t('digital_profile.time_zone')}
        value={profile.time_zone}
      />
      <DigitalProfileItem
        label={t('digital_profile.ip_location')}
        value={profile.ip_location}
      />
      <DigitalProfileItem
        label={t('digital_profile.location')}
        value={profile.location}
      />
      <DigitalProfileItem
        label={t('digital_profile.ip_organization')}
        value={profile.ip_organization}
      />
      <DigitalProfileItem
        label={t('digital_profile.user_agent')}
        value={profile.user_agent}
      />
    </Stack>
  );
};

export type FullFraudReportProps = {
  candidateName: string;
  report: FraudReport;
};

const FullFraudReport = ({ candidateName, report }: FullFraudReportProps) => {
  const { isReportPartiallyChecked } = useFraudReportStatus(report);
  return (
    <Stack spacing={3}>
      <Stack spacing={0.5} data-provides="full-report-header">
        <SectionTitle large>
          <span className="pii-blur">{candidateName}</span>
        </SectionTitle>
        <TertiaryBody>
          {t('fraud_report_run_on_date', { date: report.generated_at })}
        </TertiaryBody>
      </Stack>
      {isReportPartiallyChecked && (
        <FraudStatusBanner
          fraudReport={report}
          dataProvides="full-fraud-report-status-banner"
        />
      )}
      <PositiveSignals signals={report.positive_signals} />
      <FraudSignals signals={report.fraud_signals} />
      <DigitalProfile profile={report.digital_profile} />
    </Stack>
  );
};

export default FullFraudReport;

const SignalCountGrid = styled(Grid)`
  border: solid 1px ${FullPalette.GREY_700};
  border-radius: 5px;
`;
